import Modal from "../../../../../../components/base/Modal";
import type { ThemeClasses, TripFormData, Trip } from "../types";
import type { UseFormRegister, FieldErrors, UseFormSetValue } from "react-hook-form";
import type { TFunction } from "i18next";
import { Combobox, SwitchTrack } from "../../../../../../components/ui";
import { useRoutesStore } from "../../../../../../store/admin/routesStore";
import { useUsersStore } from "../../../../../../store/admin/usersStore";
import { useStatuses } from "../../../../../../features/catalogs/queries";
import { RouteSummary } from "./RouteSummary";
import { TripShipmentsSection } from "./TripShipmentsSection";
import { useEffect } from "react";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";

interface TripEditModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  register: UseFormRegister<TripFormData>;
  errors: FieldErrors<TripFormData>;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onReset?: () => void;
  t: TFunction;
  setValue: UseFormSetValue<TripFormData>;
  watchedValues: Partial<TripFormData>;
  tripStatus?: string;
  selectedTrip?: Trip | null;
  showShipments?: boolean;
  hideNotificationFields?: boolean;
}

export function TripEditModal({
  themeClasses,
  isDarkMode,
  register,
  errors,
  loadingSubmit,
  onClose,
  onSubmit,
  onReset,
  t,
  setValue,
  watchedValues,
  tripStatus,
  selectedTrip,
  showShipments = false,
  hideNotificationFields = false,
}: TripEditModalProps) {
  const inputClass = `w-full min-h-10 px-3 py-2 text-xs bg-app border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;
  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  const routes = useRoutesStore((state) => state.routes || []);
  const fetchRoutes = useRoutesStore((state) => state.fetchRoutes);

  const { usersLight: usersList, fetchUsersLight } = useUsersStore();
  const { data: statuses = [] } = useStatuses();
  const { isReseller } = useIsReseller();

  useEffect(() => {
    fetchRoutes({ limit: 500, page: 1 });
    // Resellers can assign carriers from any tenant; bypass the company filter
    // so the dropdown lists every user instead of just the active company.
    fetchUsersLight({ allCompanies: isReseller });
  }, [fetchRoutes, fetchUsersLight, isReseller]);

  // Get status options for trips entity
  const statusOptions = getStatusOptionsForEntity(statuses, "trips", t);

  const selectedRoute = routes.find((r) => r.id === watchedValues.route_id) || null;

  const userOptions = usersList.map((u) => ({
    value: u.id,
    label: `${u.first_name} ${u.last_name} (${u.email})`,
  }));

  const routeOptions = routes.map((r: { id: string; name?: string }) => ({
    value: r.id,
    label: r.name || r.id,
  }));

  const priorityOptions = [
    { value: "low", label: t("trips.priority.low") },
    { value: "medium", label: t("trips.priority.medium") },
    { value: "high", label: t("trips.priority.high") },
    { value: "urgent", label: t("trips.priority.urgent") },
  ];

  return (
    <Modal
      title={t("trips.modal.editTitle")}
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full max-w-4xl"
      content={
        <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-2">
          <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
            {t("trips.sections.basicInfo")}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>{t("trips.fields.title")} <span className="text-error">*</span></label>
              <input
                {...register("title", { required: t("trips.validation.titleRequired") })}
                className={`${inputClass} ${errors.title ? "border-error/30" : ""}`}
                placeholder={t("trips.fields.title")}
              />
              {errors.title && (
                <span className="text-error text-xs">{errors.title.message as string}</span>
              )}
            </div>
            <div>
              <label className={labelClass}>{t("trips.fields.status")} <span className="text-error">*</span></label>
              <input type="hidden" {...register("status_id", { required: t("trips.validation.statusRequired") })} />
              <Combobox
                options={statusOptions}
                placeholder={t("trips.modal.selectStatus")}
                value={statusOptions.find((opt) => opt.value === watchedValues.status_id) || null}
                onChange={(option: any) => setValue("status_id", option?.value || "", { shouldValidate: true })}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.status_id && (
                <span className="text-error text-xs">{errors.status_id.message as string}</span>
              )}
            </div>
            <div>
              <label className={labelClass}>{t("trips.fields.priority")} <span className="text-error">*</span></label>
              <input type="hidden" {...register("priority", { required: t("trips.validation.priorityRequired") })} />
              <Combobox
                options={priorityOptions}
                placeholder={t("trips.modal.selectPriority")}
                value={priorityOptions.find((opt) => opt.value === watchedValues.priority) || null}
                onChange={(option: any) => setValue("priority", option?.value || "", { shouldValidate: true })}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.priority && (
                <span className="text-error text-xs">{errors.priority.message as string}</span>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t("trips.fields.description")}</label>
            <textarea
              {...register("description")}
              className={`${inputClass} min-h-[80px]`}
              placeholder={t("trips.fields.description")}
            />
          </div>

          <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
            {t("trips.sections.routeInfo")}
          </h3>

          <div>
            <label className={labelClass}>{t("trips.fields.route")} <span className="text-error">*</span></label>
            <input type="hidden" {...register("route_id", { required: t("trips.validation.routeRequired") })} />
            <Combobox
              options={routeOptions}
              placeholder={t("trips.modal.selectRoute")}
              value={routeOptions.find((opt: { value: string }) => opt.value === watchedValues.route_id) || null}
              onChange={(option: any) => setValue("route_id", option?.value || "", { shouldValidate: true })}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {errors.route_id && (
              <span className="text-error text-xs">{errors.route_id.message as string}</span>
            )}
            {selectedRoute && (
              <RouteSummary
                route={selectedRoute}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                t={t}
              />
            )}
          </div>

          {showShipments && (
            <TripShipmentsSection
              value={watchedValues.shipment_ids}
              onChange={(ids) => setValue("shipment_ids", ids)}
              themeClasses={themeClasses}
              t={t}
              tripId={selectedTrip?.id ?? null}
            />
          )}

          {!hideNotificationFields && (
            <>
          {/* Notified Users */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
            {t("trips.sections.notifications")}
          </h3>

          <div className="flex flex-col gap-1.5">
            <label className={labelClass}>{t("trips.fields.notifiedUsers")}</label>
            <p className={`text-xs text-content-muted -mt-1 mb-1`}>
              {t("trips.fields.notifiedUsersHint")}
            </p>
            <Combobox
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={userOptions}
              placeholder={t("trips.modal.selectUsers")}
              value={((watchedValues.notifiedUsers as string[] | undefined) || []).map((val) => {
                const existing = userOptions.find((o) => o.value === val);
                return existing || { value: val, label: val };
              })}
              onChange={(selected) =>
                setValue(
                  "notifiedUsers",
                  selected ? selected.map((s) => s.value) : []
                )
              }
              isClearable
              isSearchable
              noOptionsMessage={() => t("trips.modal.noUsersAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Envío de correos además del push */}
          <div className="flex flex-col gap-2">
            <label className={labelClass}>{t("trips.fields.sendEmails")}</label>
            <p className={`text-xs text-content-muted -mt-1 mb-1`}>
              {t("trips.fields.sendEmailsHint")}
            </p>
            {(() => {
              const active = watchedValues.sendEmails === true;
              return (
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  onClick={() => setValue("sendEmails", !active)}
                  className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                    active
                      ? "bg-primary-500/10 border-primary-400 text-primary-600"
                      : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-mail-send-line 2xl:text-lg" aria-hidden />
                    {active
                      ? t("trips.fields.sendEmailsOn")
                      : t("trips.fields.sendEmailsOff")}
                  </span>
                  <SwitchTrack checked={active} size="sm" />
                </button>
              );
            })()}
          </div>
            </>
          )}

          <div>
            <label className={labelClass}>{t("trips.fields.comment")}</label>
            <textarea
              {...register("comment")}
              className={`${inputClass} min-h-[60px]`}
              placeholder={t("trips.fields.comment")}
            />
          </div>

          <div>
            <label className={labelClass}>Archivo del evento</label>
            <input
              type="file"
              {...register("event_file")}
              className={inputClass}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            />
            <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
              Formatos soportados: Imágenes, PDF, documentos y hojas de cálculo
            </p>
          </div>

          <div className="flex justify-between items-center pt-4">
            {(tripStatus === "selected" || tripStatus === "finalized") && onReset && (
              <button
                type="button"
                onClick={onReset}
                disabled={loadingSubmit}
                className="px-4 py-2 bg-warning hover:bg-warning text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {t("trips.actions.reset")}
              </button>
            )}
            <div className="flex space-x-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className={`flex-1 h-10 px-4 text-sm rounded-full ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} font-medium transition-colors inline-flex items-center justify-center gap-1.5`}
              >
                {t("trips.actions.cancel")}
              </button>
              <button
                type="submit"
                disabled={loadingSubmit}
                className="flex-1 h-10 px-4 text-sm rounded-full font-medium bg-primary-500 hover:bg-primary-400 text-on-primary inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingSubmit ? t("trips.actions.saving") : "Guardar evento"}
              </button>
            </div>
          </div>
        </form>
      }
    />
  );
}
